/**
 * Help content and tooltips for expense management features
 */

export interface HelpContent {
  title: string;
  description: string;
  tooltip?: string;
  examples?: string[];
  tips?: string[];
}

export const EXPENSE_HELP_CONTENT = {
  // General expense management
  expenseManagement: {
    title: "Expense Management",
    description: "Track and manage all your property-related expenses in one place. Monitor spending patterns, set up recurring expenses, and generate detailed reports for tax purposes.",
    tooltip: "Comprehensive expense tracking for your rental properties",
    tips: [
      "Keep receipts for all expenses for tax deduction purposes",
      "Categorize expenses properly to track spending patterns",
      "Set up recurring expenses to automate regular costs",
      "Review monthly reports to identify cost-saving opportunities"
    ]
  },

  // Expense categories
  categories: {
    title: "Expense Categories",
    description: "Organize your expenses using predefined categories or create custom ones for your specific needs.",
    tooltip: "Categorize expenses for better organization and reporting",
    examples: [
      "Utilities: Electricity, water, gas bills",
      "Maintenance: Repairs, upkeep, improvements",
      "Cleaning: Professional cleaning services, supplies",
      "Marketing: Listing fees, photography, advertising"
    ],
    tips: [
      "Use predefined categories when possible for consistency",
      "Create custom categories for unique property expenses",
      "Choose descriptive names for custom categories",
      "Select appropriate icons to easily identify categories"
    ]
  },

  customCategories: {
    title: "Custom Categories",
    description: "Create personalized expense categories that fit your specific property management needs.",
    tooltip: "Add categories specific to your property type or management style",
    examples: [
      "Pool Maintenance (for properties with pools)",
      "Guest Amenities (welcome gifts, toiletries)",
      "Technology (smart home devices, WiFi equipment)",
      "Landscaping (garden maintenance, seasonal decorations)"
    ],
    tips: [
      "Use clear, descriptive names",
      "Choose relevant icons for quick identification",
      "Add descriptions to clarify what expenses belong in each category",
      "Keep categories broad enough to be useful but specific enough to be meaningful"
    ]
  },

  // Expense types
  oneTimeExpense: {
    title: "One-Time Expenses",
    description: "Record expenses that occur only once, such as equipment purchases or one-off repairs.",
    tooltip: "For expenses that don't repeat regularly",
    examples: [
      "New appliance purchase",
      "Emergency repair",
      "Initial setup costs",
      "One-time professional services"
    ],
    tips: [
      "Include detailed descriptions for future reference",
      "Upload receipts when possible",
      "Associate with specific properties when applicable",
      "Consider if the expense might become recurring in the future"
    ]
  },

  fixedRecurring: {
    title: "Fixed Recurring Expenses",
    description: "Set up expenses that occur regularly with the same amount, like insurance premiums or subscription fees.",
    tooltip: "Automate tracking of regular expenses with consistent amounts",
    examples: [
      "Property insurance premiums",
      "Property management software subscriptions",
      "Fixed utility base charges",
      "Annual property taxes"
    ],
    tips: [
      "Double-check the amount before setting up",
      "Set appropriate start and end dates",
      "Choose the correct frequency (monthly, quarterly, yearly)",
      "Review and update amounts when they change"
    ]
  },

  variableRecurring: {
    title: "Variable Recurring Expenses",
    description: "Track expenses that occur regularly but with changing amounts, like utility bills or maintenance costs.",
    tooltip: "Get reminders to enter actual amounts for recurring expenses",
    examples: [
      "Monthly electricity bills",
      "Water usage charges",
      "Seasonal maintenance costs",
      "Variable cleaning fees"
    ],
    tips: [
      "Set realistic estimated ranges for budgeting",
      "Choose appropriate reminder timing",
      "Respond to reminders promptly for accurate tracking",
      "Review patterns to identify cost-saving opportunities"
    ]
  },

  // Recurring expense configuration
  frequency: {
    title: "Expense Frequency",
    description: "Choose how often the expense occurs to set up proper scheduling.",
    tooltip: "Select the interval between expense occurrences",
    examples: [
      "Weekly: Cleaning services",
      "Monthly: Utility bills, subscriptions",
      "Quarterly: Insurance payments, tax installments",
      "Yearly: Annual fees, major maintenance"
    ],
    tips: [
      "Match the frequency to your actual billing cycle",
      "Consider seasonal variations for some expenses",
      "Start with the most common frequency and adjust if needed"
    ]
  },

  reminderTiming: {
    title: "Reminder Timing",
    description: "Set when you want to be reminded to enter variable expense amounts.",
    tooltip: "Choose how many days before the due date to receive reminders",
    examples: [
      "1-2 days: For expenses you can quickly enter",
      "3-5 days: For expenses requiring receipt collection",
      "7+ days: For expenses requiring planning or approval"
    ],
    tips: [
      "Consider your schedule and availability",
      "Allow time to collect receipts and documentation",
      "Set earlier reminders for expenses requiring approval",
      "You can always enter expenses early if you receive bills sooner"
    ]
  },

  // Amount and currency
  expenseAmount: {
    title: "Expense Amount",
    description: "Enter the cost of the expense in your preferred currency.",
    tooltip: "The monetary value of the expense",
    tips: [
      "Enter the exact amount from your receipt or bill",
      "Include taxes and fees in the total amount",
      "Use the same currency consistently for accurate reporting",
      "Round to the nearest cent for precision"
    ]
  },

  currency: {
    title: "Currency Selection",
    description: "Choose the currency for your expense to ensure accurate financial tracking.",
    tooltip: "Select the currency used for this expense",
    tips: [
      "Use your local currency for most expenses",
      "Select the actual currency paid for international expenses",
      "Be consistent with currency selection for accurate reporting",
      "Consider exchange rates for foreign currency expenses"
    ]
  },

  estimatedRange: {
    title: "Estimated Amount Range",
    description: "Provide a range of expected amounts for variable recurring expenses to help with budgeting.",
    tooltip: "Set minimum and maximum expected amounts for budgeting purposes",
    examples: [
      "Electricity: $80-$150 (seasonal variation)",
      "Cleaning: $100-$200 (depending on guest turnover)",
      "Maintenance: $50-$500 (varies by issue complexity)"
    ],
    tips: [
      "Base estimates on historical data when available",
      "Consider seasonal variations in your estimates",
      "Update ranges periodically based on actual expenses",
      "Use conservative estimates to avoid budget surprises"
    ]
  },

  // Date and scheduling
  expenseDate: {
    title: "Expense Date",
    description: "Select the date when the expense occurred or when it should be recorded.",
    tooltip: "The date this expense should be recorded",
    tips: [
      "Use the date from your receipt or invoice",
      "For recurring expenses, this becomes the start date",
      "Future dates are allowed for planned expenses",
      "Consistent dating helps with accurate reporting"
    ]
  },

  startDate: {
    title: "Start Date",
    description: "Choose when the recurring expense should begin generating entries.",
    tooltip: "The first date this recurring expense will be recorded",
    tips: [
      "Use the date of your first bill or payment",
      "Consider billing cycles when setting start dates",
      "Future start dates are useful for planned recurring expenses",
      "The system will calculate subsequent dates automatically"
    ]
  },

  endDate: {
    title: "End Date (Optional)",
    description: "Set when the recurring expense should stop, or leave empty for indefinite recurring.",
    tooltip: "When to stop generating recurring expense entries",
    examples: [
      "Contract end dates",
      "Lease expiration dates",
      "Seasonal service periods",
      "Trial subscription periods"
    ],
    tips: [
      "Leave empty for ongoing expenses like utilities",
      "Set end dates for fixed-term contracts",
      "Review and extend end dates as needed",
      "Use end dates to track temporary expenses"
    ]
  },

  // Property association
  propertyAssociation: {
    title: "Property Association",
    description: "Link expenses to specific properties for detailed tracking and reporting.",
    tooltip: "Associate this expense with a specific property (optional)",
    examples: [
      "Utility bills for specific addresses",
      "Maintenance work on particular properties",
      "Property-specific insurance or taxes",
      "Individual property marketing costs"
    ],
    tips: [
      "Associate expenses with properties when possible for detailed reporting",
      "Leave unassociated for general business expenses",
      "Use property association for tax reporting by property",
      "Review property-specific expenses regularly"
    ]
  },

  // Description and notes
  expenseDescription: {
    title: "Expense Description",
    description: "Add details about the expense for future reference and record-keeping.",
    tooltip: "Additional details about this expense",
    examples: [
      "Plumber repair - kitchen sink leak",
      "Monthly electricity bill - July 2024",
      "Professional photography for listing update",
      "Emergency locksmith service - guest lockout"
    ],
    tips: [
      "Include enough detail for future reference",
      "Mention the vendor or service provider",
      "Note any special circumstances",
      "Keep descriptions concise but informative"
    ]
  },

  // Reports and analytics
  expenseReports: {
    title: "Expense Reports",
    description: "Generate detailed reports to analyze spending patterns and prepare for tax season.",
    tooltip: "Analyze your expenses with detailed reports and charts",
    examples: [
      "Monthly spending by category",
      "Year-over-year expense comparisons",
      "Property-specific expense breakdowns",
      "Tax-ready expense summaries"
    ],
    tips: [
      "Generate monthly reports to track spending trends",
      "Use category breakdowns to identify cost-saving opportunities",
      "Export reports for tax preparation",
      "Compare expenses across different properties"
    ]
  },

  dateRange: {
    title: "Date Range Selection",
    description: "Choose the time period for your expense report or analysis.",
    tooltip: "Select the period to include in your report",
    examples: [
      "Current month for monthly reviews",
      "Last quarter for quarterly analysis",
      "Full year for annual tax preparation",
      "Custom ranges for specific periods"
    ],
    tips: [
      "Use standard periods (month, quarter, year) for consistency",
      "Custom ranges are useful for specific analysis",
      "Include full months for accurate comparisons",
      "Consider seasonal patterns in your date selections"
    ]
  },

  exportOptions: {
    title: "Export Options",
    description: "Download your expense data in various formats for external use or record-keeping.",
    tooltip: "Export your expense data for external use",
    examples: [
      "CSV for spreadsheet analysis",
      "PDF for professional reports",
      "Filtered exports for specific categories",
      "Date-range exports for tax periods"
    ],
    tips: [
      "CSV format works well with accounting software",
      "PDF format is good for sharing with accountants",
      "Include all relevant fields in exports",
      "Keep exported files organized by date and purpose"
    ]
  },

  // Notifications and reminders
  expenseReminders: {
    title: "Expense Reminders",
    description: "Receive notifications when variable recurring expenses are due for entry.",
    tooltip: "Stay on top of variable expenses with automatic reminders",
    tips: [
      "Respond to reminders promptly for accurate tracking",
      "Check your notification preferences regularly",
      "Use the quick-entry form from reminder notifications",
      "Update reminder timing if needed"
    ]
  },

  notificationBadge: {
    title: "Notification Badge",
    description: "See at a glance when you have pending expense reminders or overdue entries.",
    tooltip: "Shows the number of pending expense actions",
    tips: [
      "Click the badge to see all pending reminders",
      "Address overdue reminders as soon as possible",
      "The badge updates automatically as you complete actions",
      "Use it as a daily checklist for expense management"
    ]
  },

  // Validation and errors
  validation: {
    title: "Expense Validation",
    description: "The system checks your expense data for completeness and accuracy before saving.",
    tooltip: "Automatic validation ensures data quality",
    tips: [
      "Fix validation errors before submitting",
      "Pay attention to warning messages",
      "Required fields must be completed",
      "Check amounts and dates for accuracy"
    ]
  },

  // Best practices
  bestPractices: {
    title: "Best Practices",
    description: "Follow these guidelines for effective expense management.",
    tips: [
      "Enter expenses regularly, don't wait until month-end",
      "Keep digital copies of all receipts",
      "Use consistent categorization across all expenses",
      "Review and reconcile expenses monthly",
      "Set up recurring expenses for predictable costs",
      "Use detailed descriptions for future reference",
      "Associate expenses with properties when applicable",
      "Generate reports regularly to track spending trends",
      "Keep business and personal expenses separate",
      "Backup your expense data regularly"
    ]
  }
};

// Tooltip helper functions
export const getTooltipContent = (key: keyof typeof EXPENSE_HELP_CONTENT): string => {
  const content = EXPENSE_HELP_CONTENT[key];
  return (content as any)?.tooltip || content?.description || '';
};

export const getHelpContent = (key: keyof typeof EXPENSE_HELP_CONTENT): HelpContent => {
  return EXPENSE_HELP_CONTENT[key];
};

// Quick help text for form fields
export const FIELD_HELP_TEXT = {
  amount: "Enter the expense amount (required)",
  currency: "Select the currency used for this expense",
  date: "Date when the expense occurred",
  category: "Choose an expense category (required)",
  description: "Optional details about this expense (up to 500 characters)",
  property: "Associate with a specific property (optional)",
  recurrenceType: "Choose how often this expense occurs",
  frequency: "How often the expense repeats",
  startDate: "When recurring expenses should begin",
  endDate: "When recurring expenses should stop (optional)",
  reminderDays: "Days before due date to send reminders",
  estimatedMin: "Lowest expected amount for budgeting",
  estimatedMax: "Highest expected amount for budgeting",
  customCategoryName: "Name for your custom category",
  customCategoryDescription: "Optional description of what this category covers",
  customCategoryIcon: "Icon to represent this category"
};

// Error messages with helpful context
export const HELP_ERROR_MESSAGES = {
  amountRequired: "Amount is required. Enter the expense cost in your selected currency.",
  amountInvalid: "Amount must be a positive number. Check that you've entered a valid monetary value.",
  categoryRequired: "Category is required. Select a predefined category or create a custom one.",
  dateRequired: "Date is required. Select when this expense occurred or should be recorded.",
  dateInvalid: "Date must be valid and not too far in the future. Check your date selection.",
  descriptionTooLong: "Description is too long. Keep it under 500 characters for better organization.",
  frequencyRequired: "Frequency is required for recurring expenses. Choose how often it repeats.",
  startDateRequired: "Start date is required for recurring expenses. Choose when it should begin.",
  endDateInvalid: "End date must be after start date. Check your date range.",
  estimatedRangeRequired: "Estimated range is required for variable recurring expenses.",
  estimatedRangeInvalid: "Maximum amount must be greater than minimum amount.",
  reminderDaysInvalid: "Reminder days must be between 1 and 30 days.",
  categoryNameRequired: "Category name is required. Enter a descriptive name for your custom category.",
  categoryNameTooLong: "Category name is too long. Keep it under 50 characters.",
  categoryNameDuplicate: "A category with this name already exists. Choose a different name.",
  propertyNotFound: "Selected property not found. Choose from your available properties."
};

// Contextual help for different scenarios
export const CONTEXTUAL_HELP = {
  firstTimeUser: {
    title: "Getting Started with Expense Management",
    steps: [
      "Start by adding a few recent expenses to get familiar with the system",
      "Choose appropriate categories for each expense",
      "Set up recurring expenses for regular costs like utilities",
      "Explore the reports section to see your spending patterns",
      "Create custom categories for expenses specific to your properties"
    ]
  },
  
  recurringSetup: {
    title: "Setting Up Recurring Expenses",
    steps: [
      "Choose 'Fixed' for expenses with consistent amounts (insurance, subscriptions)",
      "Choose 'Variable' for expenses with changing amounts (utilities, maintenance)",
      "Set realistic estimated ranges for variable expenses",
      "Configure reminder timing based on your schedule",
      "Review and update recurring expenses periodically"
    ]
  },
  
  categoryManagement: {
    title: "Managing Expense Categories",
    steps: [
      "Use predefined categories when they fit your needs",
      "Create custom categories for unique property expenses",
      "Choose descriptive names and appropriate icons",
      "Keep categories broad enough to be useful",
      "Review category usage in reports to optimize organization"
    ]
  },
  
  reportGeneration: {
    title: "Generating Expense Reports",
    steps: [
      "Select appropriate date ranges for your analysis",
      "Filter by categories to focus on specific expense types",
      "Use property filters for property-specific analysis",
      "Export data in the format that works best for your needs",
      "Generate regular reports to track spending trends"
    ]
  }
};